import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Button,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';
import {
  exportFoundationDisbursementsToMip,
  listFoundationDisbursements,
  markFoundationDisbursementPaid,
  revertFoundationDisbursementExport,
  type FoundationDisbursement,
} from '../../../endpoints/foundation';
import PageHeader from '../../../Components/PageHeader';
import ServerSideTable, {
  type ServerSideTableColumn,
} from '../../../Components/ServerSideTable';

export default function FoundationAwardsList() {
  const [status, setStatus] = useState('scheduled');
  const [academicYear, setAcademicYear] = useState('2025-2026');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [busy, setBusy] = useState(false);
  const [paymentRef, setPaymentRef] = useState('');

  const { data: permissions, isLoading: permsLoading } = useApiGet<string[]>(
    ['my-permissions-foundation-awards'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );
  const canView = hasUserApplicationPermission(permissions, 'foundation:view');
  const canManage = hasUserApplicationPermission(
    permissions,
    'foundation:manage'
  );

  const { data, isLoading, refetch } = useApiGet<{
    items: FoundationDisbursement[];
  }>(
    ['foundation-disbursements', status, academicYear],
    () =>
      listFoundationDisbursements({
        status: status || undefined,
        academic_year: academicYear || undefined,
      }),
    { enabled: canView, retry: 1 }
  );

  const rows: FoundationDisbursement[] = data?.items ?? [];
  const paged = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns: ServerSideTableColumn<FoundationDisbursement>[] = useMemo(
    () => [
      {
        id: 'id',
        label: 'Select',
        render: (_v, row) => (
          <input
            type="checkbox"
            checked={selected.includes(row.id)}
            onChange={(e) => {
              setSelected((prev) =>
                e.target.checked
                  ? [...prev, row.id]
                  : prev.filter((x) => x !== row.id)
              );
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        id: 'student_name',
        label: 'Student',
        render: (_v, row) => row.student_name || row.award_application_id,
      },
      { id: 'status', label: 'Status' },
      {
        id: 'amount',
        label: 'Amount',
        render: (_v, row) => Number(row.amount || 0).toFixed(2),
      },
      {
        id: 'scheduled_date',
        label: 'Scheduled',
        render: (_v, row) =>
          row.scheduled_date
            ? new Date(row.scheduled_date).toLocaleDateString()
            : '—',
      },
      {
        id: 'payment_reference',
        label: 'Reference',
        render: (_v, row) => row.payment_reference || '—',
      },
    ],
    [selected]
  );

  if (!permsLoading && !canView) {
    return <Navigate to="/foundation" replace />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader
        title="Foundation Awards"
        leftDom={undefined}
        rightDom={undefined}
        subtitle={undefined}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scheduled payments, MIP handoff, and paid status
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setSelected([]);
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="scheduled">Scheduled</MenuItem>
          <MenuItem value="exported_to_mip">Exported to MIP</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="void">Void</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Academic year"
          value={academicYear}
          onChange={(e) => setAcademicYear(e.target.value)}
        />
        <Button onClick={() => refetch()}>Refresh</Button>
        {canManage && (
          <>
            <Button
              variant="contained"
              disabled={busy || selected.length === 0}
              onClick={async () => {
                setBusy(true);
                try {
                  await exportFoundationDisbursementsToMip(selected);
                  setSelected([]);
                  await refetch();
                } finally {
                  setBusy(false);
                }
              }}
            >
              Export selected to MIP
            </Button>
            <Button
              disabled={busy || selected.length !== 1}
              onClick={async () => {
                setBusy(true);
                try {
                  await revertFoundationDisbursementExport(selected[0]);
                  setSelected([]);
                  await refetch();
                } finally {
                  setBusy(false);
                }
              }}
            >
              Revert export
            </Button>
            <TextField
              size="small"
              label="Check / payment ref"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
            />
            <Button
              disabled={busy || selected.length !== 1 || !paymentRef}
              onClick={async () => {
                setBusy(true);
                try {
                  await markFoundationDisbursementPaid(selected[0], paymentRef);
                  setSelected([]);
                  setPaymentRef('');
                  await refetch();
                } finally {
                  setBusy(false);
                }
              }}
            >
              Mark paid
            </Button>
          </>
        )}
      </Stack>
      <ServerSideTable
        data={paged}
        columns={columns}
        loading={!!isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        totalPages={Math.max(1, Math.ceil(rows.length / rowsPerPage))}
        hasNext={(page + 1) * rowsPerPage < rows.length}
        hasPrev={page > 0}
        order="asc"
        orderBy="scheduled_date"
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onSort={() => undefined}
        emptyMessage="No disbursements found."
      />
    </Container>
  );
}
